import React, { useEffect } from 'react';
import styled from 'styled-components/native';
import {
  ImageBackground,
  TouchableOpacity,
  useWindowDimensions
} from 'react-native';
import { Feather } from '@expo/vector-icons';
import { MaterialIcons } from '@expo/vector-icons';
import Colors from '../../../constants/Colors';
import { useUserContext } from '../../../context/UserContext';
import { useGetStudy } from '../../../hooks/study/useGetStudy';
import useAddStudyToFavorite from '../../../hooks/user-favorite/useAddStudyToFavorite';
import useDeleteStudyToFavorite from '../../../hooks/user-favorite/useDeleteStudyToFavorite';

const StyledContainer = styled.TouchableOpacity<{ singleColumn: boolean }>`
  border-radius: 24px;
  shadow-color: #111111;
  shadow-offset: 0 16px;
  shadow-opacity: 0.1;
  shadow-radius: 48px;
  width: ${p => (p.singleColumn ? '100%' : '330px')};
  flex-direction: row;
  height: 185px;
`;

const StyledCardView = styled(ImageBackground)`
  flex: 1;
  border-radius: 12px;
  padding: 21px 8px 21px 21px;
`;

const StyledTitleView = styled.View`
  width: 100%;
  align-items: flex-start;
`;

const StyledTitleText = styled.Text`
  font-family: 'DMSans-Bold';
  font-size: 18px;
  line-height: 23px;
  color: #f0f3ff;
  max-width: 180px;
`;

const StyledStudyLengthText = styled.Text`
  font-family: 'DMSans-Regular';
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #f0f3ff;
  margin-top: 8px;
`;

const StyledIconView = styled.View`
  width: 100%;
  position: absolute;
  align-self: flex-start;
  flex-direction: row;
  justify-content: flex-end;
`;

const StyledFeather = styled(Feather)`
  margin-left: 12px;
  margin-right: 8px;
`;

export interface StudyCardProps {
  studyId?: number;
  favorite?: boolean;
  imageUrl?: string;
  onSelect?: () => void;
  onFavorite?: () => void;
  onShare?: () => void;
}

const StudyCard: React.FC<StudyCardProps> = ({
  studyId,
  favorite,
  imageUrl,
  onSelect,
  onFavorite,
  onShare
}) => {
  const window = useWindowDimensions();
  const singleColumn = window.width < 768;

  const { user } = useUserContext();
  const {
    addStudyToFavoriteData,
    addStudyToFavorite,
    clearAddStudyToFavoriteData
  } = useAddStudyToFavorite(user?.id || -1, studyId || -1);
  const { study, getStudy } = useGetStudy(studyId || -1);

  const {
    deleteStudyToFavoriteData,
    deleteStudyToFavorite,
    clearDeleteStudyToFavoriteData
  } = useDeleteStudyToFavorite(user?.id, studyId || -1);

  useEffect(() => {
    if (studyId) getStudy();
  }, [getStudy, studyId]);
  useEffect(() => {
    if (addStudyToFavoriteData) {
      onFavorite?.();
      clearAddStudyToFavoriteData();
    }
  }, [addStudyToFavoriteData, clearAddStudyToFavoriteData, onFavorite]);

  useEffect(() => {
    if (deleteStudyToFavoriteData) {
      onFavorite?.();
      clearDeleteStudyToFavoriteData();
    }
  }, [clearDeleteStudyToFavoriteData, deleteStudyToFavoriteData, onFavorite]);

  return (
    <StyledContainer
      singleColumn={singleColumn}
      activeOpacity={0.8}
      onPress={() => onSelect?.()}
    >
      <StyledCardView
        imageStyle={{
          backgroundColor: Colors.chloePink,
          borderRadius: 12
        }}
        source={{ uri: imageUrl }}
      >
        <StyledTitleView>
          <StyledTitleText>{study?.name}</StyledTitleText>
          <StyledIconView>
            <TouchableOpacity
              onPress={() => {
                if (user?.id == null) {
                  return;
                }
                if (favorite) deleteStudyToFavorite();
                else addStudyToFavorite();
              }}
            >
              <MaterialIcons
                name={favorite ? 'favorite' : 'favorite-outline'}
                color='white'
                size={24}
              />
            </TouchableOpacity>
            <TouchableOpacity onPress={onShare}>
              <StyledFeather name='share' size={24} color='white' />
            </TouchableOpacity>
          </StyledIconView>
          <StyledStudyLengthText>
            {`Duration: ${study?.studyLength} ${
              study?.studyLength === 1 ? 'day' : 'days'
            }`}
          </StyledStudyLengthText>
        </StyledTitleView>
      </StyledCardView>
    </StyledContainer>
  );
};

export default StudyCard;
